// src/geo-manager.ts

export interface GeoUpdate {
  lat: number;
  lng: number;
  heading: number | null;
  speed: number | null;
  timestamp: number;
  trail: Array<{ lat: number; lng: number; timestamp: number }>;
}

export type GeoCallback = (update: GeoUpdate) => void;

// Trail expiry: 60 minutes in milliseconds
const TRAIL_MAX_AGE_MS = 60 * 60 * 1000;

// Minimum distance change (meters) before emitting an update
const MIN_DISTANCE_M = 5;

// Below this, GPS jitter in a parking spot looks like a heading change.
const COURSE_MIN_M = 12;

const GEO_ERROR_CODE = {
  PERMISSION_DENIED: 1,
  POSITION_UNAVAILABLE: 2,
  TIMEOUT: 3,
} as const;

const WATCH_OPTIONS = {
  highAccuracy: {
    enableHighAccuracy: true,
    // A cached fix is allowed to be this old. 5s is ~150 m behind at highway
    // speed, and follow would happily center on that stale point.
    maximumAge: 0,
    timeout: 15_000,
  },
  lowAccuracy: {
    enableHighAccuracy: false,
    maximumAge: 60_000,
    timeout: 30_000,
  },
} as const satisfies Record<string, PositionOptions>;

const BOOTSTRAP_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  maximumAge: 10 * 60_000,
  timeout: 10_000,
};

const RETRY_DELAY_MS = 15_000;

// Broadcast trails are thinned to this many points. Locally the full trail is
// kept for crisp self-rendering, but peers only need enough points to draw a
// readable polyline — and in a big room the full trail is ruinous: ~1 point/sec
// × 60 min ≈ 200 KB of JSON, re-sent to EVERY peer every 10 s. A few dozen
// cars would saturate LTE uplinks and chew through the Cloudflare TURN quota.
export const BROADCAST_TRAIL_MAX_POINTS = 150;

/**
 * Shrink a trail for broadcast: evenly sample down to maxPoints (always
 * keeping the first and last points) and round coordinates to 5 decimal
 * places (~1 m) to cut JSON bytes. Timestamps are preserved so receivers'
 * rolling-window and rebasing math still works.
 */
export function thinTrailForBroadcast(
  trail: Array<{ lat: number; lng: number; timestamp: number }>,
  maxPoints = BROADCAST_TRAIL_MAX_POINTS,
): Array<{ lat: number; lng: number; timestamp: number }> {
  const round = (n: number) => Number(n.toFixed(5));
  if (trail.length <= maxPoints) {
    return trail.map(p => ({ lat: round(p.lat), lng: round(p.lng), timestamp: p.timestamp }));
  }
  const out: Array<{ lat: number; lng: number; timestamp: number }> = [];
  for (let i = 0; i < maxPoints; i++) {
    const idx = Math.round((i * (trail.length - 1)) / (maxPoints - 1));
    const p = trail[idx]!;
    out.push({ lat: round(p.lat), lng: round(p.lng), timestamp: p.timestamp });
  }
  return out;
}

export class GeoManager {
  private watchId: number | null = null;
  private trail: Array<{ lat: number; lng: number; timestamp: number }> = [];
  private lastLat: number | null = null;
  private lastLng: number | null = null;
  private highAccuracy = true;
  private fellBackToLowAccuracy = false;
  private readonly debugEnabled = isGeoDebugEnabled();
  private retryTimer: number | null = null;
  private errorCount = 0;
  // Previous watch fix used to derive a course when the browser omits heading.
  // Bootstrap must not seed this: a cached parking-spot fix would aim the car
  // at wherever the driver actually is.
  private courseAnchor: { lat: number; lng: number } | null = null;

  constructor(private readonly onUpdate: GeoCallback) {}

  start(): void {
    if (this.watchId !== null) return;
    this.debug('start', {
      isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : undefined,
      hasGeolocation: typeof navigator !== 'undefined' && 'geolocation' in navigator,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });
    void this.logPermissionState();
    this.requestBootstrapPosition();
    this.beginWatch();
  }

  private beginWatch(): void {
    const lowAccuracy = !this.highAccuracy;
    const options = lowAccuracy ? WATCH_OPTIONS.lowAccuracy : WATCH_OPTIONS.highAccuracy;
    this.debug('watchPosition.begin', {
      mode: lowAccuracy ? 'low-accuracy-fallback' : 'high-accuracy',
      options,
    });
    this.watchId = navigator.geolocation.watchPosition(
      this.handlePosition.bind(this),
      this.handleError.bind(this),
      options,
    );
    this.debug('watchPosition.id', this.watchId);
  }

  stop(): void {
    this.clearRetryTimer();
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  getTrail(): Array<{ lat: number; lng: number; timestamp: number }> {
    return [...this.trail];
  }

  private handlePosition(pos: GeolocationPosition): void {
    const { latitude: lat, longitude: lng, heading, speed } = pos.coords;
    // Use Date.now() rather than pos.timestamp. The Tesla in-car browser (and
    // some older WebKit builds) hand back a non-Unix epoch for pos.timestamp
    // (e.g. monotonic ms since process start), which made the trail polyline
    // silently disappear because every entry failed the `Date.now() - window`
    // cutoff in MapManager.updateTrail. Always source trail timestamps from
    // the local wall clock so the cutoff math is consistent everywhere.
    const timestamp = Date.now();
    this.clearRetryTimer();
    this.debug('position', {
      lat,
      lng,
      accuracy: pos.coords.accuracy,
      altitude: pos.coords.altitude,
      altitudeAccuracy: pos.coords.altitudeAccuracy,
      heading,
      speed,
      posTimestamp: pos.timestamp,
      timestamp,
      mode: this.highAccuracy ? 'high-accuracy' : 'low-accuracy-fallback',
    });

    // Throttle: skip pushing to trail if barely moved, but still let the
    // marker move so peers and the map stay live. Without this, a Tesla
    // sitting at a red light would never broadcast the car icon nudge.
    let pushedToTrail = false;
    if (
      this.lastLat === null ||
      this.lastLng === null ||
      haversineMeters(this.lastLat, this.lastLng, lat, lng) >= MIN_DISTANCE_M
    ) {
      this.lastLat = lat;
      this.lastLng = lng;
      this.trail.push({ lat, lng, timestamp });
      pushedToTrail = true;
    }
    // Prune on every fix — not just on push — so a parked car's trail (and
    // the broadcast payload built from it) still ages out.
    const cutoff = Date.now() - TRAIL_MAX_AGE_MS;
    this.trail = this.trail.filter(p => p.timestamp >= cutoff);

    this.debug('position.dispatch', { pushedToTrail, trailLen: this.trail.length });

    this.onUpdate({
      lat,
      lng,
      heading: this.resolveHeading(lat, lng, heading ?? null),
      speed: speed ?? null,
      timestamp,
      trail: this.getTrail(),
    });
  }

  /**
   * Prefer the device course. Tesla's browser often reports heading as null
   * while still moving; once the car has actually traveled, the vector
   * between fixes is the course. Tiny jumps keep the last heading out of
   * this function (null), and the map holds whatever it already showed.
   */
  private resolveHeading(lat: number, lng: number, raw: number | null): number | null {
    const direct = normalizeHeading(raw);
    const anchor = this.courseAnchor;
    const moved = anchor == null ? 0 : haversineMeters(anchor.lat, anchor.lng, lat, lng);
    if (anchor == null || moved >= COURSE_MIN_M) {
      this.courseAnchor = { lat, lng };
    }
    if (direct != null) return direct;
    if (anchor == null || moved < COURSE_MIN_M) return null;
    return courseDegrees(anchor, { lat, lng });
  }

  private handleError(err: GeolocationPositionError): void {
    this.debug('error', {
      code: err.code,
      message: err.message,
      highAccuracy: this.highAccuracy,
      fellBackToLowAccuracy: this.fellBackToLowAccuracy,
    });
    // Desktop Chrome often returns POSITION_UNAVAILABLE when high-accuracy WiFi
    // positioning fails. Some embedded browsers instead report TIMEOUT while
    // precise positioning spins forever. Transparently retry once with
    // low-accuracy before
    // surfacing the error to the user.
    if (
      (
        err.code === GEO_ERROR_CODE.POSITION_UNAVAILABLE ||
        err.code === GEO_ERROR_CODE.TIMEOUT
      ) &&
      this.highAccuracy &&
      !this.fellBackToLowAccuracy
    ) {
      this.debug('fallback.retry', {
        reason: err.code === GEO_ERROR_CODE.TIMEOUT ? 'timeout' : 'position-unavailable',
      });
      this.fellBackToLowAccuracy = true;
      this.highAccuracy = false;
      if (this.watchId !== null) {
        navigator.geolocation.clearWatch(this.watchId);
        this.watchId = null;
      }
      this.beginWatch();
      return;
    }

    this.errorCount++;

    const messages: Record<number, string> = {
      [GEO_ERROR_CODE.PERMISSION_DENIED]:
        'Location access denied. Enable Location in your browser settings, then reload.',
      [GEO_ERROR_CODE.POSITION_UNAVAILABLE]:
        'Your browser cannot determine your location. On macOS: check System Settings → Privacy → Location Services. Make sure your browser is allowed. You can still see other people on the map.',
      [GEO_ERROR_CODE.TIMEOUT]:
        'Location timed out. Still trying…',
    };
    const msg = messages[err.code] ?? 'Location error. Check your signal.';
    document.dispatchEvent(new CustomEvent('geo:error', { detail: msg }));
    if (err.code !== GEO_ERROR_CODE.PERMISSION_DENIED) {
      this.scheduleRetry();
    }
    if (this.errorCount >= 3) {
      document.dispatchEvent(new CustomEvent('geo:gave-up'));
    }
  }

  private requestBootstrapPosition(): void {
    this.debug('getCurrentPosition.begin', { options: BOOTSTRAP_OPTIONS });
    navigator.geolocation.getCurrentPosition(
      pos => {
        this.debug('getCurrentPosition.success');
        this.handleBootstrapPosition(pos);
      },
      err => {
        this.debug('getCurrentPosition.error', {
          code: err.code,
          message: err.message,
        });
      },
      BOOTSTRAP_OPTIONS,
    );
  }

  /**
   * Seed the marker from a (possibly stale, cached) bootstrap position.
   * This MUST NOT push the point into the trail or update lastLat/lastLng,
   * or the trail polyline will draw a bogus straight line from the cached
   * location to wherever the user actually is when watchPosition first fires.
   */
  private handleBootstrapPosition(pos: GeolocationPosition): void {
    const { latitude: lat, longitude: lng, heading, speed } = pos.coords;
    this.debug('bootstrap.position', {
      lat,
      lng,
      accuracy: pos.coords.accuracy,
      posTimestamp: pos.timestamp,
    });
    this.clearRetryTimer();
    this.onUpdate({
      lat,
      lng,
      heading: heading ?? null,
      speed: speed ?? null,
      timestamp: Date.now(),
      trail: this.getTrail(),
    });
  }

  private scheduleRetry(): void {
    if (this.retryTimer !== null) return;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      this.debug('retry.tick', {
        mode: this.highAccuracy ? 'high-accuracy' : 'low-accuracy-fallback',
      });
      this.requestBootstrapPosition();
      if (this.watchId === null) {
        this.beginWatch();
      }
    }, RETRY_DELAY_MS);
    this.debug('retry.scheduled', { delayMs: RETRY_DELAY_MS });
  }

  private clearRetryTimer(): void {
    if (this.retryTimer === null) return;
    window.clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private async logPermissionState(): Promise<void> {
    if (!this.debugEnabled || !('permissions' in navigator) || !navigator.permissions?.query) return;
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      this.debug('permission', { state: status.state });
      status.onchange = () => {
        this.debug('permission.change', { state: status.state });
      };
    } catch (error) {
      this.debug('permission.query.failed', error);
    }
  }

  private debug(label: string, payload?: unknown): void {
    if (!this.debugEnabled) return;
    if (payload === undefined) {
      console.info('[teslawithme:geo]', label);
      return;
    }
    console.info('[teslawithme:geo]', label, payload);
  }
}

function isGeoDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('debugGeo') === '1') return true;
    return window.localStorage.getItem('teslawithme:debugGeo') === '1';
  } catch {
    return false;
  }
}

/** Wrap a device heading into [0, 360). Null when the browser has no course. */
export function normalizeHeading(heading: number | null): number | null {
  if (heading == null || !Number.isFinite(heading)) return null;
  return ((heading % 360) + 360) % 360;
}

/** Initial bearing in degrees clockwise from north. */
export function courseDegrees(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
): number {
  const φ1 = (from.lat * Math.PI) / 180;
  const φ2 = (to.lat * Math.PI) / 180;
  const Δλ = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Haversine distance in meters between two lat/lng points */
function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
